// src/components/InitiativeCard.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { ScoreTooltip } from './ScoreTooltips';
import { useStaffContext } from '../contexts/StaffContext';
import { SingleDatePicker } from './FilterControls';
import { formatDate } from '../utils';

const InitiativeCard = ({ 
    initiative, 
    clientId, 
    onClick,
    editable = false,
    statusOptions = [],
    onStatusChange,
    onTargetDateChange,
    'data-testid': dataTestId
}) => {
    const navigate = useNavigate();
    const { getDriDisplayName } = useStaffContext();
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const statusRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (statusRef.current && !statusRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    const handleClick = (e) => {
        if (e.target.closest('.editable-control')) return;
        
        if (onClick) {
            onClick(initiative);
        } else if (initiative.id && clientId) {
            navigate(`${ROUTES.ACCOUNTS}/${clientId}/initiatives/${initiative.id}`);
        }
    };

    const handleStatusChange = async (newStatus) => {
        if (onStatusChange && newStatus !== initiative.status) {
            setIsUpdating(true);
            try {
                await onStatusChange(initiative.id, newStatus);
            } finally {
                setIsUpdating(false);
                setShowStatusDropdown(false);
            }
        } else {
            setShowStatusDropdown(false);
        }
    };

    // Truncate description to a snippet
    const truncateText = (text, maxLength = 150) => {
        if (!text) return null;
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength).trim() + '...';
    };

    // Get status badge color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'open':
            case 'in progress':
            case 'in-progress':
                return { bg: 'rgba(110, 79, 196, 0.1)', color: 'var(--pallium-primary-purple)', border: 'var(--pallium-primary-purple)' };
            case 'on hold':
            case 'on-hold':
                return { bg: 'rgba(239, 163, 29, 0.1)', color: 'var(--pallium-warning-yellow)', border: 'var(--pallium-warning-yellow)' };
            case 'completed':
            case 'closed':
                return { bg: 'rgba(19, 185, 85, 0.1)', color: 'var(--pallium-success-green)', border: 'var(--pallium-success-green)' };
            default:
                return { bg: 'var(--pallium-stroke-lighter)', color: 'var(--pallium-mute-text)', border: 'var(--pallium-stroke-light)' };
        }
    };

    const statusStyle = getStatusColor(initiative.status);

    return (
        <div
            onClick={handleClick}
            className="p-4 rounded-lg transition-all duration-200 cursor-pointer"
            data-testid={dataTestId}
            style={{
                backgroundColor: 'var(--pallium-white)',
                border: '1px solid var(--pallium-stroke-light)',
                boxShadow: 'var(--pallium-shadow-sm)',
                opacity: isUpdating ? 0.7 : 1
            }}
            onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = 'var(--pallium-shadow-md)';
                e.currentTarget.style.borderColor = 'var(--pallium-info-cyan)';
            }}
            onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = 'var(--pallium-shadow-sm)';
                e.currentTarget.style.borderColor = 'var(--pallium-stroke-light)';
            }}
        >
            {/* Header: Icon + Initiative Name + Status */}
            <div className="flex items-start gap-3 mb-3">
                {/* Rocket/Launch Icon */}
                <div 
                    className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(0, 156, 220, 0.1)', color: 'var(--pallium-info-cyan)' }}
                >
                    <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="font-semibold truncate" style={{ color: 'var(--pallium-text-primary)' }}>
                        {initiative.name || 'Untitled Initiative'}
                    </h4>
                </div>
                {/* Status Badge - Editable or Static */}
                {editable && statusOptions.length > 0 ? (
                    <div className="relative editable-control flex-shrink-0" ref={statusRef}>
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowStatusDropdown(!showStatusDropdown); }}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all"
                            style={{ 
                                backgroundColor: statusStyle.bg, 
                                color: statusStyle.color,
                                border: '1px dashed currentColor'
                            }}
                            title="Click to change status"
                        >
                            {initiative.status || 'Set Status'}
                            <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                        {showStatusDropdown && (
                            <div 
                                className="absolute right-0 top-full mt-1 z-50 rounded-lg shadow-lg py-1 min-w-[120px]"
                                style={{ backgroundColor: 'var(--pallium-white)', border: '1px solid var(--pallium-stroke-light)' }}
                            >
                                {statusOptions.map(opt => (
                                    <button
                                        key={opt}
                                        onClick={(e) => { e.stopPropagation(); handleStatusChange(opt); }}
                                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 transition-colors"
                                        style={{ 
                                            color: opt === initiative.status ? 'var(--pallium-primary-purple)' : 'var(--pallium-text-primary)',
                                            fontWeight: opt === initiative.status ? 600 : 400
                                        }}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div 
                        className="px-2 py-1 rounded text-xs font-medium flex-shrink-0"
                        style={{ 
                            backgroundColor: statusStyle.bg, 
                            color: statusStyle.color,
                            border: `1px solid ${statusStyle.border}`
                        }}
                    >
                        {initiative.status || 'Unknown'}
                    </div>
                )}
            </div>

            {/* Description snippet */}
            {initiative.description && (
                <p className="text-xs mb-3 leading-relaxed" style={{ color: 'var(--pallium-text-secondary)' }}>
                    {truncateText(initiative.description)}
                </p>
            )}

            {/* DRI and Target Date row */}
            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>
                <div className="flex items-center gap-2">
                    <span className="font-medium" style={{ color: 'var(--pallium-text-tertiary)' }}>DRI:</span>
                    <span>{getDriDisplayName(initiative.assigned_to || initiative.dri) || 'Not assigned'}</span>
                </div>
                {/* Target Date - Editable if onTargetDateChange provided */}
                <div className="flex items-center gap-2">
                    <span className="font-medium" style={{ color: 'var(--pallium-text-tertiary)' }}>Target:</span>
                    {editable && onTargetDateChange ? (
                        <div className="editable-control" onClick={(e) => e.stopPropagation()}>
                            <SingleDatePicker
                                value={initiative.targetDate || initiative.target_date ? formatDate(initiative.targetDate || initiative.target_date) : ''}
                                onChange={(newValue) => {
                                    if (onTargetDateChange) {
                                        setIsUpdating(true);
                                        Promise.resolve(onTargetDateChange(initiative.id, newValue))
                                            .finally(() => setIsUpdating(false));
                                    }
                                }}
                                disabled={isUpdating}
                            />
                        </div>
                    ) : (
                        <span>
                            {initiative.targetDate || initiative.target_date 
                                ? formatDate(initiative.targetDate || initiative.target_date) 
                                : 'Not set'}
                        </span>
                    )}
                </div>
            </div>

            {/* Supported Goals */}
            {initiative.supportedGoals && Array.isArray(initiative.supportedGoals) && initiative.supportedGoals.length > 0 && (
                <div className="mb-3 text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>
                    <span className="font-medium" style={{ color: 'var(--pallium-text-tertiary)' }}>Supports:</span>
                    <span className="ml-1.5">{initiative.supportedGoals.join(', ')}</span>
                </div>
            )}

            {/* Footer: Action Items + Blockers */}
            <div className="flex items-center gap-4 text-xs">
                <div style={{ color: 'var(--pallium-text-secondary)' }}>
                    <span className="font-medium" style={{ color: 'var(--pallium-text-tertiary)' }}>Action Items:</span>
                    <span className="ml-1.5">{initiative.actionItemCount || 0}</span>
                </div>
                <div style={{ color: 'var(--pallium-text-secondary)' }}>
                    <span className="font-medium" style={{ color: 'var(--pallium-text-tertiary)' }}>Blockers:</span>
                    <span className="ml-1.5">{initiative.blockingIssuesCount || initiative.blockerCount || 0}</span>
                </div>
                {initiative.score !== undefined && initiative.score !== null && (
                    <div className="ml-auto flex items-center" style={{ color: 'var(--pallium-text-secondary)' }}>
                        <span className="font-medium flex items-center gap-0.5" style={{ color: 'var(--pallium-text-tertiary)' }}>
                            Momentum
                            <ScoreTooltip type="initiativeMomentum" size="sm" />
                            :
                        </span>
                        <span className="ml-1.5 font-semibold" style={{ color: 'var(--pallium-primary-purple)' }}>
                            {typeof initiative.score === 'number' ? initiative.score.toFixed(1) : initiative.score}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InitiativeCard;
